import React, { useEffect, useState } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Collapse,
  TablePagination,
} from "@mui/material";

import { Link } from "react-router-dom";

import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  TrendingDown as TrendingDownIcon,
  GetApp as GetAppIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

function RelatorioInadimplencia({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
  unidadeSelecionada = "",
}) {
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [dadosRelatorio, setDadosRelatorio] = useState(null);
  const [incluirInterrompidas, setIncluirInterrompidas] = useState(true);
  const [ordenacao, setOrdenacao] = useState("valorPendente");
  const [expandedRows, setExpandedRows] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (acao === "gerar") {
      fetchRelatorioInadimplencia();
    } else if (acao === "exportar") {
      exportarRelatorio();
    }
  }, [atualizacoes]);

  const fetchRelatorioInadimplencia = async () => {
    startLoading();
    try {
      let url = `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/getRelatorioInadimplencias/${dadosClinica._id}`;
      url += `?incluirInterrompidas=${incluirInterrompidas}&ordenacao=${ordenacao}`;

      // Adicionar filtro de datas se disponível
      if (dataFiltro && dataFiltro.dataInicial && dataFiltro.dataFinal) {
        url += `&dataInicial=${dataFiltro.dataInicial}&dataFinal=${dataFiltro.dataFinal}`;
      }

      // Sem unidade escolhida o servidor devolve as do utilizador — que para
      // quem tem acesso às duas continua a ser o consolidado de sempre.
      if (unidadeSelecionada) {
        url += `&estabelecimento=${unidadeSelecionada}`;
      }

      const response = await sendRequest(url, "GET", null, {
        Authorization: "Bearer " + auth.token,
      });

      if (response.success) {
        setDadosRelatorio(response);
        toast.success("Relatório gerado com sucesso!");
      }
    } catch (err) {
      console.error("Erro ao buscar relatório:", err);
      toast.error("Erro ao gerar relatório de inadimplências");
    } finally {
      stopLoading();
    }
  };

  const exportarRelatorio = async () => {
    if (!dadosRelatorio) {
      await fetchRelatorioInadimplencia();
      return;
    }

    try {
      const dadosExport = dadosRelatorio.data.map((item) => ({
        "Nome Cliente": item.clienteNome,
        Contacto: item.clienteContacto,
        "Nº Orçamento": item.orcamentoNumero,
        "Data Término": dayjs(item.dataTerminoTratamento).format("DD/MM/YYYY"),
        "Valor Total": item.valorTotal.toFixed(2),
        "Valor Pago": item.valorPago.toFixed(2),
        "Valor Pendente": item.valorPendente.toFixed(2),
        "% Paga":
          item.percentagemPaga != null ? `${item.percentagemPaga}%` : "0%",
        "Último Pagamento": item.dataUltimoPagamento
          ? dayjs(item.dataUltimoPagamento).format("DD/MM/YYYY")
          : "Sem pagamentos",
        "Dias desde Último Pag.": item.diasDesdeUltimoPagamento || "-",
        "Parcelas em Atraso": item.parcelasEmAtraso || 0,
        "Dias Atraso": item.diasAtrasoMaisAntigo || 0,
        "Valor em Atraso": item.valorTotalEmAtraso.toFixed(2),
        Score: item.scorePagamento || 0,
        Frequência: item.frequenciaPagamento || "-",
        Status: item.contaInterrompida ? "Interrompida" : "Ativa",
        Médico: item.medicoResponsavel?.nome || "-",
        "Tipo Cobertura": item.tipoCobertura || "-",
      }));

      const ws = XLSX.utils.json_to_sheet(dadosExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inadimplências");

      // Adicionar resumo
      const resumo = [
        ["RESUMO DO RELATÓRIO"],
        [""],
        [
          "Período",
          dataFiltro && dataFiltro.dataInicial && dataFiltro.dataFinal
            ? `${dayjs(dataFiltro.dataInicial).format(
                "DD/MM/YYYY",
              )} até ${dayjs(dataFiltro.dataFinal).format("DD/MM/YYYY")}`
            : "Todos os períodos",
        ],
        [""],
        ["Total de Clientes", dadosRelatorio.summary.totalClientes],
        ["Total de Orçamentos", dadosRelatorio.summary.totalOrcamentos],
        [
          "Valor Total Pendente",
          dadosRelatorio.summary.totalValorPendente.toFixed(2),
        ],
        [
          "Valor Total em Atraso",
          dadosRelatorio.summary.totalValorEmAtraso.toFixed(2),
        ],
        ["Ticket Médio", dadosRelatorio.summary.ticketMedio.toFixed(2)],
        ["% Média Paga", `${dadosRelatorio.summary.percentagemMediaPaga}%`],
        [
          "Contas Interrompidas",
          dadosRelatorio.summary.totalContasInterrompidas,
        ],
      ];

      const wsResumo = XLSX.utils.aoa_to_sheet(resumo);
      XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

      XLSX.writeFile(
        wb,
        `Relatorio_Inadimplencias_${dayjs().format("DD-MM-YYYY")}.xlsx`,
      );

      toast.success("Relatório exportado com sucesso!");
    } catch (err) {
      console.error("Erro ao exportar:", err);
      toast.error("Erro ao exportar relatório");
    }
  };

  const handleToggleExpand = (orcamentoId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [orcamentoId]: !prev[orcamentoId],
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "success";
    if (score >= 50) return "warning";
    return "error";
  };

  const getFrequenciaColor = (frequencia) => {
    switch (frequencia) {
      case "pontual":
        return "success";
      case "regular":
        return "info";
      default:
        return "error";
    }
  };

  const formatarValor = (valor) => {
    return `${valor.toLocaleString("pt-BR")} CVE`;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!dadosRelatorio) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="textSecondary">
          Clique em "Gerar Relatório" para visualizar os dados de inadimplência
        </Typography>
      </Box>
    );
  }

  const paginatedData = dadosRelatorio.data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Período Selecionado */}
      {dataFiltro && dataFiltro.dataInicial && dataFiltro.dataFinal && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5" }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Período: {dayjs(dataFiltro.dataInicial).format("DD/MM/YYYY")} até{" "}
            {dayjs(dataFiltro.dataFinal).format("DD/MM/YYYY")}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Mostrando orçamentos terminados neste período com valores pendentes
          </Typography>
        </Paper>
      )}

      {/* Cards de Resumo */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Clientes
              </Typography>
              <Typography variant="h4">
                {dadosRelatorio.summary.totalClientes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Valor Total Pendente
              </Typography>
              <Typography variant="h4" color="error">
                {formatarValor(dadosRelatorio.summary.totalValorPendente)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Valor em Atraso
              </Typography>
              <Typography variant="h4" color="warning.main">
                {formatarValor(dadosRelatorio.summary.totalValorEmAtraso)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                % Média Paga
              </Typography>
              <Typography variant="h4" color="success.main">
                {dadosRelatorio.summary.percentagemMediaPaga}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controles */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={incluirInterrompidas}
                  onChange={(e) => setIncluirInterrompidas(e.target.checked)}
                />
              }
              label="Incluir contas interrompidas"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Ordenar por</InputLabel>
              <Select
                value={ordenacao}
                onChange={(e) => setOrdenacao(e.target.value)}
                label="Ordenar por"
              >
                <MenuItem value="valorPendente">Valor Pendente</MenuItem>
                <MenuItem value="diasAtraso">Dias de Atraso</MenuItem>
                <MenuItem value="dataUltimoPagamento">
                  Último Pagamento
                </MenuItem>
                <MenuItem value="percentagemPaga">% Paga</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              onClick={fetchRelatorioInadimplencia}
              fullWidth
            >
              Atualizar
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={exportarRelatorio}
              fullWidth
            >
              Exportar Excel
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Cliente</TableCell>
              <TableCell>Contacto</TableCell>
              <TableCell>Orçamento</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Pago</TableCell>
              <TableCell align="right">Pendente</TableCell>
              <TableCell align="center">% Paga</TableCell>
              <TableCell align="center">Score</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Alertas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <React.Fragment key={row.orcamentoId}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleExpand(row.orcamentoId)}
                    >
                      {expandedRows[row.orcamentoId] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      component={Link}
                      to={`/clientes/${row.clienteId}-debitos`}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                        "&:hover": {
                          textDecoration: "underline",
                          color: "primary.main",
                        },
                      }}
                    >
                      {row.clienteNome}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {row.clienteContacto}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.orcamentoNumero}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {row.tipoCobertura}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {formatarValor(row.valorTotal)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: "success.main" }}>
                    {formatarValor(row.valorPago)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "error.main", fontWeight: "bold" }}
                  >
                    {formatarValor(row.valorPendente)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${row.percentagemPaga}%`}
                      size="small"
                      color={
                        row.percentagemPaga >= 80
                          ? "success"
                          : row.percentagemPaga >= 50
                            ? "warning"
                            : "error"
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.scorePagamento}
                      size="small"
                      color={getScoreColor(row.scorePagamento)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {row.contaInterrompida ? (
                      <Chip label="Interrompida" size="small" color="default" />
                    ) : (
                      <Chip
                        label={row.frequenciaPagamento}
                        size="small"
                        color={getFrequenciaColor(row.frequenciaPagamento)}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      {row.alertaCobranca && (
                        <Tooltip title="Cobrança urgente">
                          <WarningIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                      {row.elegívelParaNegociacao && (
                        <Tooltip title="Elegível para negociação">
                          <TrendingDownIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Linha expandida com detalhes */}
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={11}
                  >
                    <Collapse
                      in={expandedRows[row.orcamentoId]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ margin: 2 }}>
                        <Grid container spacing={3}>
                          {/* Informações de Pagamento */}
                          <Grid item xs={12} md={4}>
                            <Typography
                              variant="subtitle2"
                              gutterBottom
                              fontWeight="bold"
                            >
                              Informações de Pagamento
                            </Typography>
                            <Typography variant="body2">
                              <strong>Último Pagamento:</strong>{" "}
                              {row.dataUltimoPagamento
                                ? dayjs(row.dataUltimoPagamento).format(
                                    "DD/MM/YYYY",
                                  )
                                : "Sem pagamentos"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Dias desde último pag.:</strong>{" "}
                              {row.diasDesdeUltimoPagamento || "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Próximo vencimento:</strong>{" "}
                              {row.proximoVencimento
                                ? dayjs(row.proximoVencimento).format(
                                    "DD/MM/YYYY",
                                  )
                                : "-"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Parcelas em atraso:</strong>{" "}
                              {row.parcelasEmAtraso}
                            </Typography>
                            <Typography variant="body2" color="error">
                              <strong>Valor em atraso:</strong>{" "}
                              {formatarValor(row.valorTotalEmAtraso)}
                            </Typography>
                          </Grid>

                          {/* Parcelas Pendentes */}
                          <Grid item xs={12} md={4}>
                            <Typography
                              variant="subtitle2"
                              gutterBottom
                              fontWeight="bold"
                            >
                              Parcelas Pendentes ({row.parcelasPendentes.length}
                              )
                            </Typography>
                            <Box sx={{ maxHeight: 150, overflow: "auto" }}>
                              {row.parcelasPendentes.map((parcela, idx) => (
                                <Box key={idx} sx={{ mb: 1 }}>
                                  <Typography variant="body2">
                                    Parcela {parcela.nrParcela}:{" "}
                                    {formatarValor(parcela.valor)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color={
                                      parcela.emAtraso
                                        ? "error"
                                        : "textSecondary"
                                    }
                                  >
                                    Venc:{" "}
                                    {dayjs(parcela.dataVencimento).format(
                                      "DD/MM/YYYY",
                                    )}
                                    {parcela.emAtraso &&
                                      ` (${parcela.diasAtraso} dias atraso)`}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Grid>

                          {/* Últimos Pagamentos */}
                          <Grid item xs={12} md={4}>
                            <Typography
                              variant="subtitle2"
                              gutterBottom
                              fontWeight="bold"
                            >
                              Histórico Recente
                            </Typography>
                            {row.ultimosPagamentos.length > 0 ? (
                              row.ultimosPagamentos.map((pag, idx) => (
                                <Box key={idx} sx={{ mb: 1 }}>
                                  <Typography variant="body2">
                                    {dayjs(pag.data).format("DD/MM/YYYY")}:{" "}
                                    {formatarValor(pag.valor)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {pag.metodoPagamento}
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                Sem pagamentos registrados
                              </Typography>
                            )}
                          </Grid>

                          {/* Informações Adicionais */}
                          {row.contaInterrompida && row.motivoInterrupcao && (
                            <Grid item xs={12}>
                              <Typography
                                variant="subtitle2"
                                color="error"
                                gutterBottom
                              >
                                Motivo da Interrupção
                              </Typography>
                              <Typography variant="body2">
                                <strong>Tipo:</strong>{" "}
                                {row.motivoInterrupcao.tipo}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Descrição:</strong>{" "}
                                {row.motivoInterrupcao.descricao}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Data:</strong>{" "}
                                {dayjs(row.motivoInterrupcao.data).format(
                                  "DD/MM/YYYY",
                                )}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={dadosRelatorio.data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
        />
      </TableContainer>
    </Box>
  );
}

export default RelatorioInadimplencia;
